import { Box } from '@mui/material';
import type { useOwnUserNode } from '/@/hooks/nodes';
import type { GrowerCreationEntrySource } from '/@/store/tracking/events/growers';
import { getNodeScopeFilters } from '/@/utils/node-scope';
import FieldsByCropDashboardCard from '../../../components/dashboard-cards/fields-by-crop-dashboard-card';
import FieldsDashboardCard from '../../../components/dashboard-cards/fields-dashboard-card';
import GrowersDashboardCard from '../../../components/dashboard-cards/growers-dashboard-card';
import FieldsByStageDashboardCard from '../../../rvs/components/fields-by-stage-dashboard-card';
import GrowersList from '../../components/growers-list';
import NewFieldGrowersModal from '../../components/new-field-modal';
import NewGrowersModalV2 from '../../components/new-growers-modal-v2';
import { GROWERS_HELP_TOUR_IDS } from '../../growers-help-tour-ids';

export const CurrentCampaignDashboard = ({
    newGrowersModalSource,
    setNewGrowersModalSource,
    newFieldGrowersModalOpen,
    setNewFieldGrowersModalOpen,
    newFieldModalGrowerId,
    setNewFieldModalGrowerId,
    rvUserNodeQuery,
    defaultRvId,
    showFieldsByStageCard = false,
}: {
    /** Where the "Agregar productor" modal was opened from; null while it is closed. */
    newGrowersModalSource: GrowerCreationEntrySource | null;
    setNewGrowersModalSource: (source: GrowerCreationEntrySource | null) => void;
    newFieldGrowersModalOpen: boolean;
    setNewFieldGrowersModalOpen: (open: boolean) => void;
    newFieldModalGrowerId: number | undefined;
    setNewFieldModalGrowerId: (growerId: number | undefined) => void;
    rvUserNodeQuery: ReturnType<typeof useOwnUserNode>;
    /** When a Consultant creates from an RV's detail page, default the grower's RV to that RV. */
    defaultRvId?: string;
    showFieldsByStageCard?: boolean;
}) => {
    return (
        <>
            {rvUserNodeQuery.data && (
                <Box
                    id={GROWERS_HELP_TOUR_IDS.DETAIL}
                    sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1fr 1fr', xs: '1fr' }, gap: 3 }}
                >
                    <GrowersDashboardCard
                        userNodeId={rvUserNodeQuery.data?.id}
                        showButton={false}
                        nodeScope={getNodeScopeFilters('growers')}
                    />

                    <FieldsDashboardCard
                        userNodeId={rvUserNodeQuery.data?.id}
                        nodeScope={getNodeScopeFilters('growers')}
                    />

                    {showFieldsByStageCard && (
                        <>
                            <Box sx={{ gridRow: 'span 2' }}>
                                <FieldsByStageDashboardCard
                                    node={rvUserNodeQuery.data?.id}
                                    nodeScope={getNodeScopeFilters('growers')}
                                />
                            </Box>
                            <Box />
                        </>
                    )}
                    <FieldsByCropDashboardCard
                        userNodeId={rvUserNodeQuery.data?.id}
                        nodeScope={getNodeScopeFilters('growers')}
                    />
                </Box>
            )}

            {rvUserNodeQuery.data && (
                <GrowersList
                    userNodeId={rvUserNodeQuery.data?.id}
                    onAddGrower={() => setNewGrowersModalSource('growers_empty_list')}
                    setNewFieldGrowersModalOpen={setNewFieldGrowersModalOpen}
                    setNewFieldModalGrowerId={setNewFieldModalGrowerId}
                    tab='current-campaign'
                />
            )}

            {rvUserNodeQuery.data && (
                <NewGrowersModalV2
                    open={newGrowersModalSource !== null}
                    setOpen={(open) => {
                        if (!open) setNewGrowersModalSource(null);
                    }}
                    entrySource={newGrowersModalSource ?? 'growers_header'}
                    defaultRv={defaultRvId}
                    onAddFields={() => {
                        setNewFieldModalGrowerId(undefined);
                        setNewFieldGrowersModalOpen(true);
                    }}
                />
            )}
            {rvUserNodeQuery.data && (
                <NewFieldGrowersModal
                    key={newFieldModalGrowerId}
                    open={newFieldGrowersModalOpen}
                    setOpen={setNewFieldGrowersModalOpen}
                    rvUserNodeId={rvUserNodeQuery.data?.id}
                    growerUserNodeId={newFieldModalGrowerId}
                    trackEntrySource='growers-list-dashboard'
                />
            )}
        </>
    );
};
